<script setup lang="ts">
import { buildPricesItemListLd } from '~/utils/pricesSchema';

const { t, locale } = useI18n();
const localePath = useLocalePath();
const route = useRoute();

// Phones often miss the default reveal threshold during fast finger-scroll —
// sections flick past while still at opacity:0. Trigger ~280px before the
// section enters the viewport on small screens; keep default on tablet/desktop.
useReveal(
  import.meta.client && window.matchMedia('(max-width: 768px)').matches
    ? { threshold: 0, rootMargin: '0px 0px 280px 0px' }
    : undefined,
);

const isLongStay = computed(() => {
  const from = typeof route.query.from === 'string' ? route.query.from : '';
  const to = typeof route.query.to === 'string' ? route.query.to : '';
  if (!from || !to) return false;
  const a = new Date(from + 'T00:00:00Z').getTime();
  const b = new Date(to + 'T00:00:00Z').getTime();
  if (!Number.isFinite(a) || !Number.isFinite(b) || b <= a) return false;
  return Math.round((b - a) / 86_400_000) >= 30;
});

const checkinIso = computed(() =>
  typeof route.query.from === 'string' ? route.query.from : null,
);
const guestsCount = computed(() => {
  const g = Number(route.query.guests);
  return Number.isFinite(g) && g > 0 ? Math.trunc(g) : 2;
});

const canonicalUrl = computed(() => `https://georgiadream.ge${route.path}`);
const ogLocale = computed(() =>
  locale.value === 'ru' ? 'ru_RU' : locale.value === 'ka' ? 'ka_GE' : 'en_US'
);
const ogLocaleAlternate = computed(() =>
  ['en_US', 'ru_RU', 'ka_GE'].filter((l) => l !== ogLocale.value)
);
const ogImage = 'https://georgiadream.ge/images/og/home.jpg';

useSeoMeta({
  title: () => t('prices.page.seo.title'),
  description: () => t('prices.page.seo.description'),
  ogTitle: () => t('prices.page.seo.title'),
  ogDescription: () => t('prices.page.seo.description'),
  ogType: 'website',
  ogUrl: canonicalUrl,
  ogImage,
  ogImageWidth: 1200,
  ogImageHeight: 630,
  ogImageAlt: () => t('seo.home.imageAlt'),
  ogSiteName: 'Dreamland Oasis Chakvi',
  ogLocale,
  ogLocaleAlternate,
  twitterCard: 'summary_large_image',
  twitterTitle: () => t('prices.page.seo.title'),
  twitterDescription: () => t('prices.page.seo.description'),
  twitterImage: ogImage,
  twitterImageAlt: () => t('seo.home.imageAlt'),
});

// SSR-fetched apartment list + DB price ranges → ItemList JSON-LD with
// per-listing AggregateOffer. Mirrors the Apartment LD on /apartment/[id]
// so Google can attach price snippets to the /prices page in SERP.
const { data: apartmentsList } = await useAsyncData(
  () => `prices_apartments_list-${locale.value}`,
  async () => {
    const collection = ('apartments_' + locale.value) as 'apartments_en';
    return await queryCollection(collection).all();
  },
  { watch: [locale] },
);

type PriceRange = { lowPrice: number; highPrice: number; offerCount: number };
const { data: priceRanges } = await useFetch<Record<number, PriceRange>>(
  '/api/apartments/price-ranges',
  { default: () => ({}) },
);
const { data: fromPrices } = await useFetch<Record<number, number>>(
  '/api/apartments/from-prices',
  { default: () => ({}) },
);

const itemListJsonLd = computed(() => {
  const apts = apartmentsList.value || [];
  const items = apts
    .map((a) => {
      const id = Number(a.listing_id);
      if (!Number.isFinite(id)) return null;
      return {
        listingId: id,
        title: a.title || '',
        url: `https://georgiadream.ge${localePath(`/apartment/${id}`)}`,
        priceRange: priceRanges.value?.[id] ?? null,
        fromNightly: fromPrices.value?.[id] ?? null,
      };
    })
    .filter((x): x is NonNullable<typeof x> => x !== null);

  if (!items.length) return '';

  return JSON.stringify(
    buildPricesItemListLd({
      canonicalUrl: canonicalUrl.value,
      name: t('prices.page.seo.title'),
      items,
    }),
  );
});

useHead({
  link: [{ rel: 'canonical', href: canonicalUrl }],
  script: [
    {
      type: 'application/ld+json',
      innerHTML: itemListJsonLd,
    },
  ],
});

useBreadcrumbsLd(() => [
  { name: t('nav.home'), path: '/' },
  { name: t('nav.prices'), path: '/prices' },
]);

const apartmentsAnchor = computed(() => localePath('/') + '#apartments');

// Responsive LCP preload for the hero — see apartment/[id].vue for the why.
// The same getSizes() result feeds both the rel=preload link and the hero
// <img> srcset/sizes, so the preloaded URL always matches the painted one
// (NuxtImg serializes IPX modifiers in a different order → URL mismatch →
// double download).
const $img = useImage();
const heroPreload = computed(() =>
  $img.getSizes('/images/complex/complex1.jpg', {
    sizes: 'xs:100vw sm:100vw md:100vw lg:1280px xl:1536px',
    modifiers: { format: 'webp', quality: 60 },
  })
);
// No-srcset fallback for legacy browsers; mid-tier width.
const heroFallbackSrc = computed(() =>
  $img('/images/complex/complex1.jpg', { format: 'webp', quality: 60, width: 1280 })
);
useHead(() => ({
  link: [
    {
      rel: 'preload',
      as: 'image',
      imagesrcset: heroPreload.value.srcset,
      imagesizes: heroPreload.value.sizes,
      fetchpriority: 'high',
    },
  ],
}));
</script>

<template>
  <div>
    <!-- ── HERO ─────────────────────────────────────────────────────── -->
    <section class="relative flex min-h-[40vh] w-full items-center overflow-hidden bg-slate-900 py-10 text-white sm:py-14 md:min-h-[52vh] md:py-20">
      <!-- background photo. Plain <img> bound to the same getSizes() result
           as the rel=preload in script — keeps preload and painted URL
           identical (see comment above heroPreload). -->
      <img
        :src="heroFallbackSrc"
        :srcset="heroPreload.srcset"
        :sizes="heroPreload.sizes"
        :alt="$t('prices.page.heroImageAlt')"
        loading="eager"
        fetchpriority="high"
        class="absolute inset-0 h-full w-full object-cover"
      >
      <!-- dark gradient for text contrast -->
      <div
        aria-hidden="true"
        class="pointer-events-none absolute inset-0 bg-gradient-to-b from-slate-900/70 via-slate-900/55 to-slate-900/85"
      />
      <!-- brand atmosphere accents -->
      <div
        aria-hidden="true"
        class="pointer-events-none absolute inset-0 bg-[radial-gradient(ellipse_at_top_left,rgba(212,175,55,0.22),transparent_55%)]"
      />
      <div
        aria-hidden="true"
        class="pointer-events-none absolute inset-x-0 bottom-0 h-px bg-gradient-to-r from-transparent via-amber-400/60 to-transparent"
      />

      <div class="container relative z-10 text-center">
        <h1 class="mt-6 font-display text-3xl italic leading-tight text-balance [overflow-wrap:anywhere] text-white drop-shadow sm:text-4xl md:mt-10 md:text-5xl lg:text-6xl">
          {{ t('prices.page.h1') }}
        </h1>
        <p class="mx-auto mt-4 max-w-3xl text-base text-white/90 sm:text-lg md:mt-6 md:text-xl">
          {{ t('prices.page.intro') }}
        </p>
      </div>
    </section>

    <!-- ── CALCULATOR ───────────────────────────────────────────────── -->
    <!-- First content block after the hero: let visitors price their own
         stay immediately, before the static reference table below. -->
    <PricesQuoteCalculator>
      <template #results>
        <PricesLongStayContact
          v-if="isLongStay"
          :checkin-iso="checkinIso"
          :guests="guestsCount"
        />
        <PricesQuoteRows v-else />
      </template>
    </PricesQuoteCalculator>

    <!-- ── TABLE ────────────────────────────────────────────────────── -->
    <section class="relative overflow-hidden pt-12 md:pt-20 pb-2 md:pb-4 bg-white dark:bg-slate-900">
      <!-- Top-right spot mirrors the calculator's bottom-right spot in the
           section above: at the shared seam the two arcs merge into one
           continuous right-edge glow. -->
      <div
        aria-hidden="true"
        class="pointer-events-none absolute -top-32 -right-16 h-80 w-80 rounded-full bg-amber-200/25 blur-3xl dark:bg-amber-500/10"
      />
      <div data-reveal class="relative container">
        <div class="text-center max-w-2xl mx-auto mb-4">
          <div class="flex items-center justify-center gap-3 font-body text-xs uppercase tracking-[0.22em] text-slate-400 dark:text-slate-500">
            <span class="inline-block h-px w-8 bg-amber-400" aria-hidden="true" />
            <span>{{ t('prices.summary.eyebrow') }}</span>
            <span class="inline-block h-px w-8 bg-amber-400" aria-hidden="true" />
          </div>
        </div>
        <PricesPriceTable variant="full" :show-toggle="false" />
      </div>
    </section>

    <PricesWhatsIncluded />
    <PricesBookingRules />
    <PricesGuestPerks />
    <PricesDlComparison />

    <!-- ── FINAL CTA ────────────────────────────────────────────────── -->
    <section
      class="relative overflow-hidden bg-gradient-to-b from-white via-amber-50/30 to-white py-12 md:py-16 dark:from-slate-900 dark:via-slate-900 dark:to-slate-900"
    >
      <div
        aria-hidden="true"
        class="pointer-events-none absolute -top-32 -right-16 h-80 w-80 rounded-full bg-amber-200/25 blur-3xl dark:bg-amber-500/10"
      />
      <div data-reveal class="relative container mx-auto max-w-3xl px-4 text-center">
        <h2 class="font-display text-3xl italic leading-tight text-slate-900 md:text-4xl dark:text-white">
          {{ t('prices.dl.caption') }}
        </h2>
        <div class="mt-8">
          <a
            :href="apartmentsAnchor"
            class="group inline-flex items-center gap-2 rounded-full bg-amber-400 px-7 py-3 font-body text-sm font-semibold uppercase tracking-[0.18em] text-slate-900 shadow-sm transition-colors hover:bg-amber-300"
          >
            {{ t('prices.cta.viewApartments') }}
            <Icon
              name="lucide:arrow-right"
              class="size-4 transition-transform duration-300 group-hover:translate-x-0.5"
              aria-hidden="true"
            />
          </a>
        </div>
      </div>
    </section>
    <StickyBookingBar />
  </div>
</template>
